/**
 * Configuration Override
 *
 * Applies the `configurationOverride` a host page passes to `Gladly.init()` on
 * top of the configuration loaded from the CDN (or handed over by V1), so a
 * third-party A/B framework can serve two widget variants from one embed, one
 * endpoint and one stored configuration:
 *
 *   Gladly.init({ appId, configurationOverride: { v2: { legacyUI: true } } });
 *   Gladly.init({ appId, configurationOverride: { v2: { display: 'BUTTON' } } });
 *
 * `ConfigurationOverride` (domain/configuration.ts) is the whole overridable
 * surface, and this module reads nothing outside it. That is the security
 * property, and it is the reason to read named values rather than to merge the
 * override and subtract the keys that must not reach the configuration: the
 * override is page-supplied input applied to an already validated
 * configuration, and the keys that must never come from a page include the chat
 * transport URLs and the Beacon manifest URL.
 *
 * To make another value overridable, add it to the type and read it here.
 *
 * Note that V1 has an override too, and it is not this one: V1 reads
 * `deviceConfig.configurationOverride`, the second argument to `Gladly.init`
 * (web/src/configuration/configureStore.js:34).
 */

import {
  type ConfigurationOverride,
  LAUNCHER_DISPLAY,
  type LauncherDisplay,
  type ServerConfiguration,
} from '@/domain/configuration';
import { logger } from '@/lib/logger';

/**
 * Returns the server configuration with the host page's override applied.
 * Returns the configuration unchanged when there is nothing usable to apply.
 *
 * Each key is checked on its own: a refused key does not stop the other.
 */
export function applyConfigurationOverride(
  serverConfig: ServerConfiguration,
  override: ConfigurationOverride | null | undefined,
): ServerConfiguration {
  // Optional chaining covers every value shape a JavaScript caller can pass: a
  // string, an array or a primitive `v2` all yield undefined here rather than
  // throwing out of init() and leaving the page with no widget at all. It does
  // not cover a throwing accessor or a Proxy, which a page can only use to break
  // its own widget.
  const display = readDisplay(override?.v2?.display);
  const legacyUI = readLegacyUI(override?.v2?.legacyUI);

  // The V1 hand-off skips validServerConfiguration, so a configuration can reach
  // us with no `v2` at all (bootstrap.ts reads `_serverConfig.v2?.launchers` for
  // the same reason). Building one that holds only these values would leave every
  // other V2 reader without its defaults.
  if ((display === undefined && legacyUI === undefined) || !serverConfig.v2) {
    return serverConfig;
  }

  return {
    ...serverConfig,
    v2: {
      ...serverConfig.v2,
      ...(display === undefined ? {} : { display }),
      ...(legacyUI === undefined ? {} : { legacyUI }),
    },
  };
}

function readDisplay(value: unknown): LauncherDisplay | undefined {
  if (value === undefined || isLauncherDisplay(value)) return value;
  return refuse('display', 'BAR or BUTTON', value);
}

function readLegacyUI(value: unknown): boolean | undefined {
  if (value === undefined || typeof value === 'boolean') return value;
  // The string 'true' is the shape an A/B tool's variable substitution usually
  // produces, so this is the likely case.
  return refuse('legacyUI', 'a boolean', value);
}

/**
 * Reports a refused key and returns undefined, so a reader can `return refuse(...)`.
 * `logger.error` rather than `warn`, which prints nothing in a production build: a
 * refused override is the integrator's mistake and they have to see it. Only the
 * type of the value is logged, never the page-supplied value itself.
 */
function refuse(key: string, expected: string, value: unknown): undefined {
  logger.error(`[bootstrap] ignoring configurationOverride: v2.${key} must be ${expected}`, {
    received: typeof value,
  });
  return undefined;
}

const DISPLAYS = new Set<string>(Object.values(LAUNCHER_DISPLAY));

function isLauncherDisplay(value: unknown): value is LauncherDisplay {
  return typeof value === 'string' && DISPLAYS.has(value);
}
